'use client';

import { useEffect, useState } from 'react';
import { DomainSuggestion } from '../lib/types';

interface Step3Props {
  domains: DomainSuggestion[];
  selectedDomain: string;
  onSelect: (domain: string) => void;
  onDomainsUpdate: (domains: DomainSuggestion[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?\.[a-z]{2,}$/;

export default function Step3DomainResults({
  domains,
  selectedDomain,
  onSelect,
  onDomainsUpdate,
  onNext,
  onBack,
}: Step3Props) {
  const [checking, setChecking] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [customDomain, setCustomDomain] = useState('');
  const [customStatus, setCustomStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  useEffect(() => {
    let cancelled = false;

    async function checkDomains() {
      setChecking(true);
      const updated = [...domains];

      for (let i = 0; i < updated.length; i++) {
        if (cancelled) return;
        try {
          const res = await fetch(
            `/api/check-domain?domain=${encodeURIComponent(updated[i].domain)}`
          );
          const data = await res.json();
          updated[i] = { ...updated[i], available: data.available };
        } catch {
          updated[i] = { ...updated[i], available: false };
        }
        onDomainsUpdate([...updated]);
      }

      setChecking(false);
    }

    if (domains.some((d) => d.available === null)) {
      checkDomains();
    } else {
      setChecking(false);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableCount = domains.filter((d) => d.available === true).length;

  const normalizedCustom = customDomain.toLowerCase().trim();
  const isValidFormat = DOMAIN_REGEX.test(normalizedCustom);

  async function handleCheckCustom() {
    if (!isValidFormat) return;
    setCustomStatus('checking');
    try {
      const res = await fetch(`/api/check-domain?domain=${encodeURIComponent(normalizedCustom)}`);
      const data = await res.json();
      if (data.available) {
        setCustomStatus('available');
        onSelect(normalizedCustom);
      } else {
        setCustomStatus('taken');
      }
    } catch {
      setCustomStatus('taken');
    }
  }

  const isCustomSelected = selectedDomain === normalizedCustom && customStatus === 'available';

  return (
    <div className="animate-fade-in max-w-lg mx-auto">
      <h2 className="text-2xl font-bold mb-2">Choisissez votre domaine</h2>
      <p className="text-muted mb-6">
        {checking
          ? 'Vérification de la disponibilité en cours...'
          : `${availableCount} domaine${availableCount > 1 ? 's' : ''} disponible${availableCount > 1 ? 's' : ''}`}
      </p>

      <div className="space-y-2.5">
        {domains.map((d, i) => {
          const isChecking = d.available === null;
          const isAvailable = d.available === true;
          const isSelected = selectedDomain === d.domain;

          return (
            <button
              key={d.domain}
              onClick={() => {
                if (isAvailable) {
                  onSelect(d.domain);
                  setCustomStatus('idle');
                }
              }}
              disabled={!isAvailable}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-lg border-2 transition-all duration-200 text-left cursor-pointer disabled:cursor-not-allowed animate-slide-in ${
                isSelected && !isCustomSelected
                  ? 'border-primary bg-primary-light/50 shadow-sm'
                  : isAvailable
                    ? 'border-border hover:border-primary/50 hover:bg-gray-50'
                    : 'border-border bg-gray-50 opacity-60'
              }`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected && !isCustomSelected
                      ? 'border-primary bg-primary'
                      : 'border-border'
                  }`}
                >
                  {isSelected && !isCustomSelected && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className={`font-mono text-sm sm:text-base ${!isAvailable && !isChecking ? 'line-through' : ''}`}>
                  {d.domain}
                </span>
              </div>

              <span className="shrink-0 ml-2">
                {isChecking ? (
                  <span className="text-muted text-sm flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
                  </span>
                ) : isAvailable ? (
                  <span className="text-success text-sm font-medium animate-pulse-check">
                    &#10003; Disponible
                  </span>
                ) : (
                  <span className="text-danger text-sm">&#10007; Pris</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom domain section */}
      <div className="mt-6 pt-6 border-t border-border">
        {!showCustom ? (
          <button
            onClick={() => setShowCustom(true)}
            className="w-full text-center text-sm text-primary hover:text-primary-hover font-medium transition-colors cursor-pointer py-2"
          >
            Aucun domaine ne vous convient ? Saisissez le v&ocirc;tre
          </button>
        ) : (
          <div className="animate-fade-in">
            <p className="text-sm font-medium mb-3">Saisir un domaine personnalisé</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={customDomain}
                onChange={(e) => {
                  setCustomDomain(e.target.value.toLowerCase().replace(/\s/g, ''));
                  setCustomStatus('idle');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isValidFormat) handleCheckCustom();
                }}
                placeholder="monentreprise.com"
                className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm bg-white"
              />
              <button
                onClick={handleCheckCustom}
                disabled={!isValidFormat || customStatus === 'checking'}
                className="px-5 py-3 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium text-sm rounded-lg transition-colors cursor-pointer shrink-0"
              >
                {customStatus === 'checking' ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </span>
                ) : (
                  'Vérifier'
                )}
              </button>
            </div>

            {customDomain.length > 0 && !isValidFormat && (
              <p className="text-xs text-danger mt-2">
                Format attendu : nom.extension (ex: monentreprise.com)
              </p>
            )}

            {customStatus === 'available' && (
              <div className="mt-3 flex items-center gap-2 text-success text-sm font-medium animate-fade-in">
                <span className="animate-pulse-check">&#10003;</span>
                <span><strong className="font-mono">{normalizedCustom}</strong> est disponible et sélectionné !</span>
              </div>
            )}

            {customStatus === 'taken' && (
              <div className="mt-3 flex items-center gap-2 text-danger text-sm animate-fade-in">
                <span>&#10007;</span>
                <span><strong className="font-mono">{normalizedCustom}</strong> est déjà pris. Essayez un autre nom.</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg border border-border text-muted hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Retour
        </button>
        <button
          onClick={onNext}
          disabled={!selectedDomain}
          className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer"
        >
          Configurer les emails
        </button>
      </div>
    </div>
  );
}